import { Capacitor, registerPlugin } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { Toast } from '@capacitor/toast';
import { App as CapacitorApp } from '@capacitor/app';
import axios from 'axios';
import api from '../services/api';

let initialized = false;
const FCMTokenPlugin = Capacitor.isNativePlatform()
  ? registerPlugin('FCMTokenPlugin')
  : null;

// Actionable notification category for approval requests (Approve / Reject).
const APPROVAL_CATEGORY = 'APPROVAL_REQUEST';

// Map the request kind (sent in notification data as `requestType`) to its
// approve/reject REST endpoint.
const APPROVAL_ENDPOINTS = {
  leave: { base: '/leave-requests', method: 'post' },
  checkout: { base: '/checkout-requests', method: 'post' },
  'extra-hours': { base: '/extra-hours-requests', method: 'post' },
  'attendance-correction': { base: '/attendance-corrections', method: 'put' },
};

async function toast(message) {
  try {
    await Toast.show({ text: message, duration: 'long' });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('toast:', message);
  }
}

// Approve/Reject a request directly from a notification action.
async function handleApprovalAction(actionId, data = {}) {
  const decision =
    actionId === 'APPROVE' ? 'approve' : actionId === 'REJECT' ? 'reject' : null;
  if (!decision) return false;

  const endpoint = APPROVAL_ENDPOINTS[data.requestType];
  const requestId = data.requestId;
  if (!endpoint || !requestId) {
    console.warn('⚠️  Approval action missing endpoint/requestId', data);
    return false;
  }

  const token =
    localStorage.getItem('token') ||
    (axios.defaults.headers.common['Authorization']?.replace('Bearer ', ''));
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  try {
    await api[endpoint.method](`${endpoint.base}/${decision}/${requestId}`, {}, { headers });
    await toast(decision === 'approve' ? '✅ Request approved' : '🚫 Request rejected');
    return true;
  } catch (e) {
    const status = e?.response?.status;
    if (status === 401) {
      await toast('Please open the app and sign in to act on this request.');
    } else {
      await toast(`Couldn't ${decision} the request. Open the app to try again.`);
    }
    console.error('❌ Approval action failed:', status, e?.response?.data || e?.message);
    return false;
  }
}

// Android notification action buttons open the app via a coachems:// deep link.
// Parse it and run the same approve/reject flow (or navigate on a body tap).
async function handleApprovalDeepLink(url) {
  if (!url || url.indexOf('coachems://approval') !== 0) return;
  try {
    const u = new URL(url);
    const action = u.searchParams.get('action'); // approve | reject | open
    const requestType = u.searchParams.get('type');
    const requestId = u.searchParams.get('id');

    if (action === 'approve' || action === 'reject') {
      await handleApprovalAction(action === 'approve' ? 'APPROVE' : 'REJECT', {
        requestType,
        requestId,
      });
    } else if (requestType) {
      const route = requestType === 'leave' ? '/leave-management' : '/manager-dashboard';
      window.location.assign(route);
    }
  } catch (e) {
    console.warn('⚠️  Failed to handle approval deep link:', e?.message || e);
  }
}

async function registerDeviceToken(tokenValue, platform) {
  if (!tokenValue) return;

  // Always get the current token from localStorage or axios defaults
  // This ensures we use the latest token even if the user logged in after initPush was called
  const currentToken =
    localStorage.getItem('token') ||
    (axios.defaults.headers.common['Authorization']?.replace('Bearer ', ''));

  const headers = {};
  if (currentToken) {
    headers.Authorization = `Bearer ${currentToken}`;
  }

  if (!headers.Authorization) {
    console.warn('⚠️  No auth token available for device registration. User may not be logged in yet.');
    // Retry after a delay if user might be logging in
    setTimeout(() => {
      const retryToken = localStorage.getItem('token');
      if (retryToken) {
        console.log('🔄 Retrying device registration...');
        const retryHeaders = { Authorization: `Bearer ${retryToken}` };
        api
          .post(
            '/devices/register',
            {
              token: tokenValue,
              platform,
            },
            { headers: retryHeaders }
          )
          .then(() => {
            console.log('✅ Device token registered successfully (retry)');
          })
          .catch((err) => {
            console.error('❌ Device register failed (retry):', err?.response?.data || err?.message);
          });
      }
    }, 2000);
    return;
  }

  console.log('📤 Registering device token with server...');
  const response = await api.post(
    '/devices/register',
    {
      token: tokenValue,
      platform,
    },
    { headers }
  );

  console.log('✅ Device token registered successfully:', response.data);
}

export async function initPush(authToken) {
  if (initialized) return;
  initialized = true;

  if (!Capacitor.isNativePlatform()) {
    // Skip on web
    return;
  }

  const platform = Capacitor.getPlatform();

  try {
    console.log('🔔 Initializing push notifications...');
    
    let permStatus = await PushNotifications.checkPermissions();
    console.log('📋 Current permission status:', permStatus);
    
    if (permStatus.receive !== 'granted') {
      console.log('📝 Requesting push notification permissions...');
      permStatus = await PushNotifications.requestPermissions();
      console.log('📋 Permission request result:', permStatus);
      
      if (permStatus.receive !== 'granted') {
        console.warn('⚠️  Push notification permission denied');
        return; // Permission denied
      }
    }

    console.log('📝 Registering for push notifications...');
    await PushNotifications.register();
    console.log('✅ Push notification registration initiated');

    // Register the actionable "Approve / Reject" category so approval-request
    // notifications show inline action buttons (iOS) the user can tap without
    // opening the app.
    try {
      await PushNotifications.registerActionTypes({
        types: [
          {
            id: APPROVAL_CATEGORY,
            actions: [
              { id: 'APPROVE', title: 'Approve' },
              { id: 'REJECT', title: 'Reject', destructive: true },
            ],
          },
        ],
      });
      console.log('✅ Registered Approve/Reject notification actions');
    } catch (e) {
      console.warn('⚠️  Failed to register notification action types:', e?.message || e);
    }

    // Android: approval action buttons open the app through a coachems:// deep
    // link. Handle both a warm open and a cold start (launch URL).
    try {
      CapacitorApp.addListener('appUrlOpen', ({ url }) => handleApprovalDeepLink(url));
      const launch = await CapacitorApp.getLaunchUrl();
      if (launch && launch.url) handleApprovalDeepLink(launch.url);
    } catch (e) {
      /* @capacitor/app unavailable — ignore */
    }

    PushNotifications.addListener('registration', async (token) => {
      try {
        if (platform === 'ios') {
          console.log('📱 APNs token received (iOS). Waiting for FCM token from Firebase...');
          return;
        }

        console.log('📱 Push notification token received:', token.value?.substring(0, 20) + '...');
        await registerDeviceToken(token.value, platform);
      } catch (e) {
        console.error('❌ Device register failed:', {
          status: e?.response?.status,
          statusText: e?.response?.statusText,
          data: e?.response?.data,
          message: e?.message,
          platform: Capacitor.getPlatform()
        });
        
        // If unauthorized, retry after a short delay (user might be logging in)
        if (e?.response?.status === 401) {
          console.log('🔄 Will retry registration after user logs in...');
        }
      }
    });

    PushNotifications.addListener('registrationError', (error) => {
      // eslint-disable-next-line no-console
      console.error('Push registration error:', error);
    });

    if (platform === 'ios' && FCMTokenPlugin) {
      try {
        const result = await FCMTokenPlugin.getToken();
        if (result?.value) {
          console.log('📱 FCM token received (initial):', result.value?.substring(0, 20) + '...');
          await registerDeviceToken(result.value, platform);
        }

        // Listen for token refreshes
        FCMTokenPlugin.addListener('token', async ({ value }) => {
          try {
            console.log('🔄 FCM token refreshed:', value?.substring(0, 20) + '...');
            await registerDeviceToken(value, platform);
          } catch (err) {
            console.error('❌ Failed to register refreshed FCM token:', err?.message || err);
          }
        });
      } catch (err) {
        console.error('❌ Failed to fetch FCM token on iOS:', err?.message || err);
      }
    }

    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      // Foreground received - can show in-app toast if desired
      // Keep minimal to avoid UX noise
    });

    PushNotifications.addListener('pushNotificationActionPerformed', async (action) => {
      const data = action?.notification?.data || {};
      const actionId = action?.actionId; // 'APPROVE' | 'REJECT' | 'tap' (default)

      // Approve/Reject tapped directly on the notification.
      if (actionId === 'APPROVE' || actionId === 'REJECT') {
        await handleApprovalAction(actionId, data);
        return;
      }

      // Default tap on the body — take the user to the relevant approvals screen.
      if (data.requestType && APPROVAL_ENDPOINTS[data.requestType]) {
        const route =
          data.requestType === 'leave' ? '/leave-management' : '/manager-dashboard';
        try {
          window.location.assign(route);
        } catch (e) {
          /* noop */
        }
      }
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('Push init failed:', e?.message || e);
  }
}


